import { getApp, getApps, initializeApp } from "firebase/app";
import {
  deleteToken as firebaseDeleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

import {
  registerDeviceToken,
  unregisterDeviceToken,
} from "./notificationApiClient";

const STORAGE_KEYS = Object.freeze({
  TOKEN: "admin_hny_fcm_web_token",
  LAST_SYNC_AT: "admin_hny_fcm_web_token_last_sync_at",
  PENDING_TOKEN: "admin_hny_fcm_web_pending_token",
});

const FIREBASE_REQUIRED_KEYS = Object.freeze([
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
]);

const FIREBASE_SDK_VERSION = "10.14.1";

let objCachedContext = null;
let promiseServiceWorkerRegistration = null;
let strServiceWorkerConfigSignature = null;

const getFirebaseConfigFromEnv = () => ({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
});

const getVapidKey = () =>
  `${process.env.REACT_APP_FIREBASE_VAPID_KEY || ""}`.trim();

const getMissingFirebaseConfigKeys = (objConfig = {}) =>
  FIREBASE_REQUIRED_KEYS.filter((strKey) => !`${objConfig[strKey] || ""}`.trim());

const isLocalhostHostName = (strHost = "") =>
  ["localhost", "127.0.0.1", "[::1]"].includes(`${strHost || ""}`.trim());

const hasWindow = () => typeof window !== "undefined";

const isHttpsOrLocalhost = () => {
  if (!hasWindow()) return false;
  const strProtocol = `${window.location?.protocol || ""}`.toLowerCase();
  return strProtocol === "https:" || isLocalhostHostName(window.location?.hostname);
};

const getStoredToken = () =>
  hasWindow()
    ? `${window.localStorage.getItem(STORAGE_KEYS.TOKEN) || ""}`.trim()
    : "";

const storeToken = (strToken = "") => {
  if (!hasWindow()) return;
  const strNormalizedToken = `${strToken || ""}`.trim();
  if (!strNormalizedToken) return;
  window.localStorage.setItem(STORAGE_KEYS.TOKEN, strNormalizedToken);
  window.localStorage.setItem(STORAGE_KEYS.LAST_SYNC_AT, new Date().toISOString());
};

const clearStoredToken = () => {
  if (!hasWindow()) return;
  window.localStorage.removeItem(STORAGE_KEYS.TOKEN);
  window.localStorage.removeItem(STORAGE_KEYS.LAST_SYNC_AT);
  window.localStorage.removeItem(STORAGE_KEYS.PENDING_TOKEN);
};

const getPendingToken = () =>
  hasWindow()
    ? `${window.localStorage.getItem(STORAGE_KEYS.PENDING_TOKEN) || ""}`.trim()
    : "";

const storePendingToken = (strToken = "") => {
  if (!hasWindow()) return;
  const normalizedToken = `${strToken || ""}`.trim();
  if (!normalizedToken) return;
  window.localStorage.setItem(STORAGE_KEYS.PENDING_TOKEN, normalizedToken);
};

const clearPendingToken = () => {
  if (!hasWindow()) return;
  window.localStorage.removeItem(STORAGE_KEYS.PENDING_TOKEN);
};

const buildServiceWorkerUrl = ({
  objFirebaseConfig = {},
  strVapidKey = "",
} = {}) => {
  const params = new URLSearchParams({
    firebaseSdkVersion: FIREBASE_SDK_VERSION,
    apiKey: objFirebaseConfig.apiKey || "",
    authDomain: objFirebaseConfig.authDomain || "",
    projectId: objFirebaseConfig.projectId || "",
    storageBucket: objFirebaseConfig.storageBucket || "",
    messagingSenderId: objFirebaseConfig.messagingSenderId || "",
    appId: objFirebaseConfig.appId || "",
    measurementId: objFirebaseConfig.measurementId || "",
    vapidKey: strVapidKey || "",
  });

  return `/firebase-messaging-sw.js?${params.toString()}`;
};

const ensureServiceWorkerRegistration = async ({
  objFirebaseConfig = {},
  strVapidKey = "",
} = {}) => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  const strSignature = JSON.stringify({
    ...objFirebaseConfig,
    strVapidKey,
  });
  if (
    promiseServiceWorkerRegistration &&
    strServiceWorkerConfigSignature === strSignature
  ) {
    return promiseServiceWorkerRegistration;
  }

  strServiceWorkerConfigSignature = strSignature;
  promiseServiceWorkerRegistration = navigator.serviceWorker.register(
    buildServiceWorkerUrl({
      objFirebaseConfig,
      strVapidKey,
    })
  );
  return promiseServiceWorkerRegistration;
};

const resolveMessagingContext = async () => {
  if (objCachedContext) return objCachedContext;

  if (typeof window === "undefined") {
    objCachedContext = {
      isSupported: false,
      reason: "WINDOW_NOT_AVAILABLE",
    };
    return objCachedContext;
  }

  if (!("Notification" in window)) {
    objCachedContext = {
      isSupported: false,
      reason: "NOTIFICATION_API_UNAVAILABLE",
    };
    return objCachedContext;
  }

  if (!("serviceWorker" in navigator)) {
    objCachedContext = {
      isSupported: false,
      reason: "SERVICE_WORKER_UNAVAILABLE",
    };
    return objCachedContext;
  }

  const blnMessagingSupported = await isSupported().catch(() => false);
  if (!blnMessagingSupported) {
    objCachedContext = {
      isSupported: false,
      reason: "FIREBASE_MESSAGING_UNSUPPORTED",
    };
    return objCachedContext;
  }

  const objFirebaseConfig = getFirebaseConfigFromEnv();
  const arrMissingConfigKeys = getMissingFirebaseConfigKeys(objFirebaseConfig);
  const strVapidKey = getVapidKey();

  if (arrMissingConfigKeys.length) {
    objCachedContext = {
      isSupported: false,
      reason: "MISSING_FIREBASE_CONFIG",
      arrMissingConfigKeys,
    };
    return objCachedContext;
  }

  if (!strVapidKey) {
    objCachedContext = {
      isSupported: false,
      reason: "MISSING_FIREBASE_VAPID_KEY",
      arrMissingConfigKeys: ["vapidKey"],
    };
    return objCachedContext;
  }

  const objFirebaseApp = getApps().length
    ? getApp()
    : initializeApp(objFirebaseConfig);
  const objMessaging = getMessaging(objFirebaseApp);

  objCachedContext = {
    isSupported: true,
    objFirebaseConfig,
    strVapidKey,
    objMessaging,
  };
  return objCachedContext;
};

const requestBrowserNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "granted") {
    return "granted";
  }
  const permission = await Notification.requestPermission();
  return permission;
};

const syncAdminFcmToken = async ({ forceSync = false } = {}) => {
  const objContext = await resolveMessagingContext();
  if (!objContext?.isSupported) {
    return {
      success: false,
      status: "unsupported",
      reason: objContext?.reason || "UNKNOWN",
    };
  }

  if (!isHttpsOrLocalhost()) {
    return {
      success: false,
      status: "https_required",
    };
  }

  if (Notification.permission !== "granted") {
    return {
      success: false,
      status: "permission_not_granted",
      permission: Notification.permission,
    };
  }

  const objServiceWorkerRegistration = await ensureServiceWorkerRegistration({
    objFirebaseConfig: objContext.objFirebaseConfig,
    strVapidKey: objContext.strVapidKey,
  });

  let strToken = "";
  try {
    strToken = await getToken(objContext.objMessaging, {
      vapidKey: objContext.strVapidKey,
      serviceWorkerRegistration: objServiceWorkerRegistration || undefined,
    });
  } catch (error) {
    return {
      success: false,
      status: "token_fetch_failed",
      error: `${error?.message || "Unable to fetch push token"}`,
    };
  }

  const strCurrentToken = `${strToken || ""}`.trim();
  if (!strCurrentToken) {
    return {
      success: false,
      status: "token_not_available",
    };
  }

  const strStoredToken = getStoredToken();
  if (!forceSync && strStoredToken === strCurrentToken) {
    clearPendingToken();
    return {
      success: true,
      status: "unchanged",
      token: strCurrentToken,
    };
  }

  try {
    await registerDeviceToken(strCurrentToken);
  } catch (error) {
    storePendingToken(strCurrentToken);
    return {
      success: false,
      status: "sync_failed",
      error: `${error?.message || "Request failed"}`,
      token: strCurrentToken,
    };
  }

  if (strStoredToken && strStoredToken !== strCurrentToken) {
    try {
      await unregisterDeviceToken(strStoredToken);
    } catch (error) {
      // ignore old token cleanup errors after new token sync
    }
  }

  storeToken(strCurrentToken);
  clearPendingToken();

  return {
    success: true,
    status: strStoredToken === strCurrentToken ? "resynced" : "updated",
    token: strCurrentToken,
  };
};

const enableAdminBrowserNotifications = async () => {
  const objContext = await resolveMessagingContext();
  if (!objContext?.isSupported) {
    return {
      success: false,
      status: "unsupported",
      reason: objContext?.reason || "UNKNOWN",
    };
  }

  if (!isHttpsOrLocalhost()) {
    return {
      success: false,
      status: "https_required",
    };
  }

  const permission = await requestBrowserNotificationPermission();
  if (permission !== "granted") {
    return {
      success: false,
      status: "permission_denied",
      permission,
    };
  }

  return syncAdminFcmToken({ forceSync: true });
};

const disconnectAdminBrowserNotifications = async () => {
  const strStoredToken = getStoredToken();
  const strPendingToken = getPendingToken();
  if (strStoredToken) {
    try {
      await unregisterDeviceToken(strStoredToken);
    } catch (error) {
      // ignore backend cleanup failure while disconnecting locally
    }
  }
  if (strPendingToken && strPendingToken !== strStoredToken) {
    try {
      await unregisterDeviceToken(strPendingToken);
    } catch (error) {
      // ignore backend cleanup failure while disconnecting locally
    }
  }

  const objContext = await resolveMessagingContext();
  if (objContext?.isSupported && Notification.permission === "granted") {
    try {
      await firebaseDeleteToken(objContext.objMessaging);
    } catch (error) {
      // ignore deleteToken failures and still cleanup local/back-end state
    }
  }

  clearStoredToken();
  return {
    success: true,
  };
};

const startForegroundMessageListener = async (callback) => {
  const objContext = await resolveMessagingContext();
  if (!objContext?.isSupported) {
    return () => {};
  }
  return onMessage(objContext.objMessaging, (payload) => {
    if (typeof callback === "function") {
      callback(payload);
    }
  });
};

const getAdminBrowserNotificationStatus = async () => {
  const objFirebaseConfig = getFirebaseConfigFromEnv();
  const arrMissingConfigKeys = getMissingFirebaseConfigKeys(objFirebaseConfig);
  const strVapidKey = getVapidKey();
  const objContext = await resolveMessagingContext();

  return {
    isSupported: Boolean(objContext?.isSupported),
    reason: objContext?.reason || null,
    isHttpsOrLocalhost: isHttpsOrLocalhost(),
    notificationPermission:
      typeof window !== "undefined" && "Notification" in window
        ? Notification.permission
        : "unsupported",
    hasStoredToken: Boolean(getStoredToken()),
    storedToken: getStoredToken(),
    pendingToken: getPendingToken(),
    lastSyncedAt: hasWindow()
      ? window.localStorage.getItem(STORAGE_KEYS.LAST_SYNC_AT) || null
      : null,
    hasFirebaseConfig: arrMissingConfigKeys.length === 0,
    hasVapidKey: Boolean(strVapidKey),
    arrMissingConfigKeys: [
      ...arrMissingConfigKeys,
      ...(strVapidKey ? [] : ["vapidKey"]),
    ],
  };
};

export {
  enableAdminBrowserNotifications,
  syncAdminFcmToken,
  disconnectAdminBrowserNotifications,
  startForegroundMessageListener,
  getAdminBrowserNotificationStatus,
};
